/**
 * 素材上传组件
 * 支持上传本地视频 + 字幕文件
 */
import React, { useCallback, useState, useRef } from 'react';
import { createMaterial, storeVideoBlob } from '../../../db/shadowDb';
import { parseSubtitleFile } from '../services/subtitleParser';

interface MaterialUploadProps {
  onUploadComplete?: (materialId: string) => void;
  onCancel?: () => void;
}

interface UploadState {
  status: 'idle' | 'selecting' | 'processing' | 'error';
  error?: string;
  videoFile?: File;
  subtitleFile?: File;
  title?: string;
}

export function MaterialUpload({ onUploadComplete, onCancel }: MaterialUploadProps) {
  const [state, setState] = useState<UploadState>({ status: 'idle' });
  const videoInputRef = useRef<HTMLInputElement>(null);
  const subtitleInputRef = useRef<HTMLInputElement>(null);

  const handleVideoSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 验证视频格式
    if (!file.type.startsWith('video/')) {
      setState((prev) => ({
        ...prev,
        status: 'error',
        error: '请选择视频文件（MP4、WebM 等）',
      }));
      return;
    }

    // 验证文件大小（限制 100MB，避免 IndexedDB 配额问题）
    const MAX_VIDEO_SIZE = 100 * 1024 * 1024;
    if (file.size > MAX_VIDEO_SIZE) {
      setState((prev) => ({
        ...prev,
        status: 'error',
        error: `视频文件过大（${Math.round(file.size / 1024 / 1024)}MB），请控制在 100MB 以内`,
      }));
      return;
    }

    // 默认标题使用文件名
    const title = file.name.replace(/\.[^/.]+$/, '');
    setState((prev) => ({
      ...prev,
      videoFile: file,
      title: prev.title || title,
      error: undefined,
    }));
  }, []);

  const handleSubtitleSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 验证字幕格式
    const ext = file.name.toLowerCase().split('.').pop();
    if (ext !== 'srt' && ext !== 'vtt') {
      setState((prev) => ({
        ...prev,
        status: 'error',
        error: '请选择 SRT 或 VTT 字幕文件',
      }));
      return;
    }

    setState((prev) => ({
      ...prev,
      subtitleFile: file,
      error: undefined,
    }));
  }, []);

  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({ ...prev, title: e.target.value }));
  }, []);

  const handleSubmit = useCallback(async () => {
    const { videoFile, subtitleFile, title } = state;

    if (!videoFile) {
      setState((prev) => ({ ...prev, error: '请选择视频文件' }));
      return;
    }

    if (!subtitleFile) {
      setState((prev) => ({ ...prev, error: '请选择字幕文件' }));
      return;
    }

    if (!title?.trim()) {
      setState((prev) => ({ ...prev, error: '请输入素材标题' }));
      return;
    }

    setState((prev) => ({ ...prev, status: 'processing', error: undefined }));

    try {
      // 解析字幕
      const subtitles = await parseSubtitleFile(subtitleFile);
      if (subtitles.length === 0) {
        throw new Error('字幕文件为空或格式不正确');
      }

      // 存储视频 Blob
      const videoBlobId = await storeVideoBlob(videoFile);

      // 计算视频时长（从字幕推断）
      const durationMs = subtitles[subtitles.length - 1].endMs;

      // 创建素材记录
      const materialId = await createMaterial({
        title: title.trim(),
        videoBlobId,
        subtitles,
        durationMs,
        progress: { practicedSentences: [], fluencyScores: {} },
        retellingHistory: [],
      });

      setState({ status: 'idle' });
      onUploadComplete?.(materialId);
    } catch (err) {
      setState((prev) => ({
        ...prev,
        status: 'error',
        error: `上传失败：${(err as Error).message}`,
      }));
    }
  }, [state, onUploadComplete]);

  const isReady = state.videoFile && state.subtitleFile && state.title?.trim();

  return (
    <div className="material-upload">
      <div className="upload-header">
        <h3 className="upload-title">上传新素材</h3>
        {onCancel && (
          <button type="button" className="close-btn" onClick={onCancel}>
            ✕
          </button>
        )}
      </div>

      <div className="upload-form">
        {/* 标题输入 */}
        <div className="form-group">
          <label className="form-label">素材标题</label>
          <input
            type="text"
            className="form-input"
            placeholder="输入素材标题..."
            value={state.title || ''}
            onChange={handleTitleChange}
          />
        </div>

        {/* 视频上传 */}
        <div className="form-group">
          <label className="form-label">视频文件</label>
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            className="file-input"
            onChange={handleVideoSelect}
          />
          <button
            type="button"
            className="file-btn"
            onClick={() => videoInputRef.current?.click()}
          >
            {state.videoFile ? (
              <span className="file-selected">
                📹 {state.videoFile.name}
              </span>
            ) : (
              <span>选择视频文件</span>
            )}
          </button>
        </div>

        {/* 字幕上传 */}
        <div className="form-group">
          <label className="form-label">字幕文件</label>
          <input
            ref={subtitleInputRef}
            type="file"
            accept=".srt,.vtt"
            className="file-input"
            onChange={handleSubtitleSelect}
          />
          <button
            type="button"
            className="file-btn"
            onClick={() => subtitleInputRef.current?.click()}
          >
            {state.subtitleFile ? (
              <span className="file-selected">
                📝 {state.subtitleFile.name}
              </span>
            ) : (
              <span>选择字幕文件 (.srt / .vtt)</span>
            )}
          </button>
        </div>

        {/* 错误提示 */}
        {state.error && (
          <div className="upload-error">{state.error}</div>
        )}

        {/* 提交按钮 */}
        <button
          type="button"
          className={`submit-btn ${isReady ? 'ready' : ''}`}
          disabled={!isReady || state.status === 'processing'}
          onClick={handleSubmit}
        >
          {state.status === 'processing' ? '处理中...' : '创建素材'}
        </button>
      </div>
    </div>
  );
}
